/**
 * Đo lọt lòng — phần hình học thuần (không phụ thuộc scene/i18n → unit test được).
 * Chỉ dùng khung bao THẾ GIỚI của 2 tấm nên áp được cho mọi kiểu geometry (box/mesh/extrusion).
 *  - Pháp tuyến tấm = trục mỏng nhất của khung bao (mỏng < 0.6 × 2 trục còn lại).
 *  - 2 tấm tách rời theo pháp tuyến → đo mặt-mặt ('inner'): vách↔vách, kệ↔kệ, cánh↔hậu.
 *  - 2 tấm cùng mặt phẳng (chồng nhau theo pháp tuyến) → đo khe cạnh-cạnh ('gap'): cánh↔cánh, hậu↔hậu.
 */
import { Box3, Vector3 } from 'three';

export type Axis = 'x' | 'y' | 'z';

const AXES: Axis[] = ['x', 'y', 'z'];
const THIN_RATIO = 0.6;

export type InnerDimSolve =
  | { ok: true; kind: 'inner' | 'gap'; axis: Axis; pt1: Vector3; pt2: Vector3; dist: number }
  | { ok: false; reason: 'noDir' | 'notParallel' | 'overlap' };

/** Trục pháp tuyến của tấm từ khung bao; null nếu không có trục nào đủ mỏng (tấm uốn, đặt xiên…). */
export function panelNormal(bb: Box3): Axis | null {
  const size = bb.getSize(new Vector3());
  for (const a of AXES) {
    const others = AXES.filter((o) => o !== a).map((o) => size[o]);
    if (size[a] < Math.min(...others) * THIN_RATIO) return a;
  }
  return null;
}

/** Khoảng hở giữa 2 đoạn [min,max] trên trục; âm = chồng nhau. */
function sep(bb1: Box3, bb2: Box3, a: Axis): number {
  return Math.max(bb2.min[a] - bb1.max[a], bb1.min[a] - bb2.max[a]);
}

/**
 * Giải đo lọt lòng giữa 2 khung bao (cùng đơn vị). `eps` = dung sai coi 2 tấm "chạm nhau" là tách rời
 * (khe 0) — tránh sai số float khi 2 tấm hậu giáp mí.
 */
export function solveInnerDim(bb1: Box3, bb2: Box3, eps = 0): InnerDimSolve {
  const n1 = panelNormal(bb1);
  const n2 = panelNormal(bb2);
  if (!n1 || !n2) return { ok: false, reason: 'noDir' };
  if (n1 !== n2) return { ok: false, reason: 'notParallel' };

  let axis: Axis;
  let kind: 'inner' | 'gap';
  if (sep(bb1, bb2, n1) >= -eps) {
    axis = n1;
    kind = 'inner';
  } else {
    // cùng mặt phẳng → lấy trục trong mặt phẳng có khe nhỏ nhất (2 tấm lệch chéo: khe gần hơn)
    const cands = AXES.filter((a) => a !== n1 && sep(bb1, bb2, a) >= -eps);
    if (!cands.length) return { ok: false, reason: 'overlap' };
    axis = cands.reduce((best, a) => (sep(bb1, bb2, a) < sep(bb1, bb2, best) ? a : best));
    kind = 'gap';
  }

  // 2 đầu đo: mặt/cạnh đối diện trên `axis`; trục còn lại đặt giữa vùng giao (không giao → giữa 2 tâm)
  const [lo, hi] = bb1.max[axis] <= bb2.min[axis] + eps ? [bb1, bb2] : [bb2, bb1];
  const a1 = lo.max[axis];
  const a2 = Math.max(hi.min[axis], a1);
  const pt1 = new Vector3();
  const pt2 = new Vector3();
  for (const o of AXES) {
    if (o === axis) continue;
    const gLo = Math.max(bb1.min[o], bb2.min[o]);
    const gHi = Math.min(bb1.max[o], bb2.max[o]);
    const mid = gLo <= gHi ? (gLo + gHi) / 2 : (bb1.min[o] + bb1.max[o] + bb2.min[o] + bb2.max[o]) / 4;
    pt1[o] = mid;
    pt2[o] = mid;
  }
  pt1[axis] = a1;
  pt2[axis] = a2;
  return { ok: true, kind, axis, pt1, pt2, dist: a2 - a1 };
}
